
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/components/ui/sonner';
import { canStaffManageUser, logStaffUserAction } from './utils';

// Assign course to user (staff can only assign to users they manage)
export const assignCourseToUserByStaff = async (
  userId: string,
  courseId: string,
  courseType: string
): Promise<boolean> => {
  try {
    console.log('[Course Assignment] Starting assignment:', { userId, courseId, courseType });
    
    // Check if staff can manage this user
    const canManage = await canStaffManageUser(userId);
    console.log('[Course Assignment] Permission check result:', canManage);
    
    if (!canManage) {
      const errorMsg = 'You do not have permission to manage this user';
      console.error('[Course Assignment] Permission denied:', errorMsg);
      toast.error(errorMsg);
      return false;
    }

    // Check if assignment already exists
    const { data: existingAssignment, error: checkError } = await supabase
      .from('course_assignments')
      .select('id')
      .eq('user_id', userId)
      .eq('course_id', courseId)
      .maybeSingle();

    if (checkError) {
      console.error('[Course Assignment] Error checking existing assignment:', checkError);
      toast.error(`Database error: ${checkError.message}`);
      return false;
    }

    if (existingAssignment) {
      console.log('[Course Assignment] Course already assigned');
      toast.info('Course is already assigned to this user');
      return false;
    }

    // Get current user
    const { data: { user: currentUser } } = await supabase.auth.getUser();
    if (!currentUser) {
      console.error('[Course Assignment] No authenticated user found');
      toast.error('Authentication error - please log in again');
      return false;
    }

    console.log('[Course Assignment] Inserting assignment for user:', userId);
    const { error, data } = await supabase
      .from('course_assignments')
      .insert({
        user_id: userId,
        course_id: courseId,
        assigned_by: currentUser.id,
        status: 'active',
        final_grade: 0
      })
      .select()
      .single();

    if (error) {
      console.error('[Course Assignment] Database error:', {
        code: error.code,
        message: error.message,
        details: error.details,
        hint: error.hint
      });
      
      // Provide specific error messages
      if (error.code === '42501') {
        toast.error('Permission denied: You cannot assign courses to this user');
      } else if (error.code === '23505') {
        toast.error('This course is already assigned to the user');
      } else {
        toast.error(`Failed to assign course: ${error.message}`);
      }
      return false;
    }

    console.log('[Course Assignment] Assignment successful:', data);

    // Log the action
    await logStaffUserAction(
      userId,
      'course_assigned',
      {
        course_id: courseId,
        course_type: courseType,
        assignment_id: data?.id
      }
    );

    toast.success('Course assigned successfully');
    return true;
  } catch (error) {
    console.error('[Course Assignment] Unexpected error:', error);
    const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
    toast.error(`Failed to assign course: ${errorMessage}`);
    return false;
  }
};

// Remove course assignment
export const unassignCourseFromUserByStaff = async (
  userId: string,
  courseId: string
): Promise<boolean> => {
  try {
    console.log('[Course Unassignment] Starting unassignment:', { userId, courseId });
    
    // Check if staff can manage this user
    const canManage = await canStaffManageUser(userId);
    console.log('[Course Unassignment] Permission check result:', canManage);
    
    if (!canManage) {
      const errorMsg = 'You do not have permission to manage this user';
      console.error('[Course Unassignment] Permission denied:', errorMsg);
      toast.error(errorMsg);
      return false;
    }

    const { error } = await supabase
      .from('course_assignments')
      .delete()
      .eq('user_id', userId)
      .eq('course_id', courseId);

    if (error) {
      console.error('[Course Unassignment] Database error:', {
        code: error.code,
        message: error.message,
        details: error.details
      });
      toast.error(`Failed to unassign course: ${error.message}`);
      return false;
    }

    console.log('[Course Unassignment] Unassignment successful');

    // Log the action
    await logStaffUserAction(
      userId,
      'course_unassigned',
      {
        course_id: courseId
      }
    );

    toast.success('Course unassigned successfully');
    return true;
  } catch (error) {
    console.error('[Course Unassignment] Unexpected error:', error);
    const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
    toast.error(`Failed to unassign course: ${errorMessage}`);
    return false;
  }
};

// Get user's course assignments
export const getUserCourseAssignmentsByStaff = async (userId: string) => {
  try {
    // Check if staff can manage this user
    const canManage = await canStaffManageUser(userId);
    if (!canManage) {
      return [];
    }

    const { data, error } = await supabase
      .from('course_assignments')
      .select('*')
      .eq('user_id', userId)
      .order('assigned_date', { ascending: false });

    if (error) {
      console.error('Error fetching user assignments:', error);
      return [];
    }

    return data || [];
  } catch (error) {
    console.error('Error in getUserCourseAssignmentsByStaff:', error);
    return [];
  }
};

// Bulk operations for efficiency
export const bulkAssignCoursesToUsersByStaff = async (
  userIds: string[],
  courseIds: string[]
): Promise<boolean> => {
  try {
    // Verify staff can manage all users
    const canManageAll = await Promise.all(
      userIds.map(userId => canStaffManageUser(userId))
    );

    if (!canManageAll.every(canManage => canManage)) {
      toast.error('You do not have permission to manage some of the selected users');
      return false;
    }

    const assignments = [];
    const currentUser = (await supabase.auth.getUser()).data.user;

    for (const userId of userIds) {
      for (const courseId of courseIds) {
        assignments.push({
          user_id: userId,
          course_id: courseId,
          assigned_by: currentUser?.id,
          status: 'active',
          final_grade: 0
        });
      }
    }

    const { error } = await supabase
      .from('course_assignments')
      .insert(assignments);

    if (error) {
      console.error('Error bulk assigning courses:', error);
      toast.error('Failed to bulk assign courses');
      return false;
    }

    // Log bulk actions
    for (const userId of userIds) {
      await logStaffUserAction(
        userId,
        'bulk_course_assigned',
        {
          course_ids: courseIds,
          bulk_operation: true
        }
      );
    }

    toast.success(`Successfully assigned ${courseIds.length} courses to ${userIds.length} users`);
    return true;
  } catch (error) {
    console.error('Error in bulk assign:', error);
    toast.error('Failed to bulk assign courses');
    return false;
  }
};
